package com.arena_web.ui.sections.people.mobile

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.PeopleUiState
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.widgets.InlineSpinner
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.people.COMMIT_LIKE
import com.arena_web.ui.sections.people.COMMIT_LIKE_BUTTON
import com.arena_web.ui.sections.people.COMMIT_SKIP
import com.arena_web.ui.sections.people.COMMIT_SKIP_BUTTON
import com.arena_web.ui.sections.people.PeopleCardStack
import com.arena_web.ui.sections.profile.edit.GradientBorderRing
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

// Figma 8188:196120 — mobile People swipe screen. Renders the existing
// PeopleCardStack at a viewport-friendly scale and lays out a compact 55 px
// action row below (Figma 8188:196153) without the desktop SquadUpPanel chrome.
@Composable
fun MobilePeopleContent(
    state: PeopleUiState,
    sportName: String,
    gatedSwipes: Boolean,
    onLike: () -> Unit,
    onSkip: () -> Unit,
    onSwipeAttempt: () -> Unit,
    onInfo: () -> Unit,
    onReload: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(top = 12.px, bottom = 110.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        val current = state.queue.firstOrNull()
        when {
            state.isLoading && current == null -> MobileLoading()
            current == null -> MobileEmpty(state.hasMore, state.error, onReload)
            else -> {
                var pendingCommit by remember(current.userId) {
                    mutableStateOf<String?>(null)
                }

                Box(
                    modifier = Modifier
                        .flexGrow(1)
                        .fillMaxWidth()
                        // Let the flex item shrink on short viewports. NB: no
                        // overflow:hidden here — the swipe fly-off animation
                        // moves the card horizontally off-screen and must not
                        // be clipped.
                        .minHeight(0.px),
                    contentAlignment = Alignment.Center,
                ) {
                    // PeopleCardStack reads `--scale` via the s() helpers, so
                    // overriding it locally rescales the whole card without
                    // touching the component's internals. 0.7 keeps the deck
                    // clear of the action row on standard phones.
                    Box(modifier = Modifier.styleModifier { property("--scale", "0.7px") }) {
                        PeopleCardStack(
                            current = current,
                            sportName = sportName,
                            pendingCommit = pendingCommit,
                            onDragCommit = { pendingCommit = it },
                            onAnimationDone = {
                                if (gatedSwipes) {
                                    onSwipeAttempt()
                                } else {
                                    when (pendingCommit) {
                                        COMMIT_LIKE, COMMIT_LIKE_BUTTON -> onLike()
                                        COMMIT_SKIP, COMMIT_SKIP_BUTTON -> onSkip()
                                    }
                                }
                                pendingCommit = null
                            },
                        )
                    }
                }

                MobileActionRow(
                    enabled = !state.isSwiping && pendingCommit == null,
                    onSkip = { pendingCommit = COMMIT_SKIP_BUTTON },
                    onInfo = onInfo,
                    onLike = { pendingCommit = COMMIT_LIKE_BUTTON },
                )

                if (state.error != null) {
                    SpanText(
                        state.error,
                        modifier = Modifier
                            .fontSize(13.px)
                            .padding(top = 8.px)
                            .color(AppColors.Error),
                    )
                }
            }
        }
    }
}

// Figma 8188:196153 — 3 buttons, 55×55, 29.7 px gap. Reject + Accept are
// translucent discs with red/green glyphs; Info uses the project gradient
// border with a blue tint inside (mirrors the desktop CircleActionButton
// design but at mobile dimensions).
@Composable
private fun MobileActionRow(
    enabled: Boolean,
    onSkip: () -> Unit,
    onInfo: () -> Unit,
    onLike: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().gap(30.px).padding(top = 12.px, bottom = 16.px),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        MobileCircleButton(
            iconPath = AppIcons.Close,
            iconColor = ACTION_RED,
            enabled = enabled,
            onClick = onSkip,
        )
        MobileCircleButton(
            iconPath = AppIcons.Information,
            iconColor = ACTION_INFO,
            iconSize = 22,
            enabled = enabled,
            onClick = onInfo,
        )
        MobileCircleButton(
            iconPath = AppIcons.Check,
            iconColor = ACTION_GREEN,
            enabled = enabled,
            onClick = onLike,
        )
    }
}

private const val ACTION_RED = "#FF4242"
private const val ACTION_GREEN = "#7BFA6F"
private const val ACTION_INFO = "#BED6F5"
private const val ACTION_DISC_BG = "rgba(11, 22, 46, 0.85)"

@Composable
private fun MobileCircleButton(
    iconPath: String,
    iconColor: String,
    enabled: Boolean,
    onClick: () -> Unit,
    iconSize: Int = 26,
) {
    Box(
        modifier = Modifier
            .size(55.px)
            .borderRadius(50.percent)
            .then(if (enabled) Modifier.cursor(Cursor.Pointer).onClick { onClick() } else Modifier)
            .styleModifier {
                property("position", "relative")
                property("background", ACTION_DISC_BG)
                property("backdrop-filter", "blur(4px)")
                if (!enabled) property("opacity", "0.5")
            },
        contentAlignment = Alignment.Center,
    ) {
        GradientBorderRing(radiusPx = 100)
        Box(
            modifier = Modifier
                .size(iconSize.px)
                .styleModifier {
                    property("background-color", iconColor)
                    property("mask", "url('$iconPath') no-repeat center / contain")
                    property("-webkit-mask", "url('$iconPath') no-repeat center / contain")
                },
        )
    }
}

@Composable
private fun MobileLoading() {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center,
    ) {
        InlineSpinner(size = 64.px)
    }
}

@Composable
private fun MobileEmpty(hasMore: Boolean, error: String?, onReload: () -> Unit) {
    if (hasMore && error == null) {
        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            InlineSpinner(size = 64.px)
        }
        return
    }
    val t = LocalAppStrings.current
    val (title, subtitle) = if (error != null) {
        t.peopleErrorLoad to error
    } else {
        t.peopleCaughtUpTitle to t.peopleCaughtUpSubtitle
    }
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(24.px)
            .gap(20.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(modifier = Modifier.flexGrow(1))
        Icon(src = AppIcons.EmptyMatches, modifier = Modifier.size(120.px))
        SpanText(
            title,
            modifier = Modifier
                .fontSize(20.px)
                .fontWeight(500)
                .lineHeight(26.px)
                .color(AppColors.Text.Secondary)
                .styleModifier { property("text-align", "center") },
        )
        SpanText(
            subtitle,
            modifier = Modifier
                .fontSize(14.px)
                .lineHeight(22.px)
                .color(AppColors.Text.Secondary)
                .styleModifier { property("text-align", "center") },
        )
        Box(
            modifier = Modifier
                .height(40.px)
                .borderRadius(100.px)
                .padding(leftRight = 24.px)
                .cursor(Cursor.Pointer)
                .backgroundColor(Color("#0061FF"))
                .onClick { onReload() },
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                t.peopleRefresh,
                modifier = Modifier
                    .fontSize(14.px)
                    .fontWeight(500)
                    .color(Color.white),
            )
        }
        Box(modifier = Modifier.flexGrow(1))
    }
}
